<script setup lang="ts">
/**
 * The rate book in force (services/api/config/pricing.toml):
 * training $0.05/min + $0.25 launch · tokens $0.20 in / $0.90 out per 1M ·
 * teacher $1.00 / 1M tokens · embedding $0.02 / 1M tokens ·
 * predictions $1.00 / 1 000 rows · storage $0.10 GB-mo · $5 monthly credit.
 * Only items the rate book prices are listed. Index storage has no measured
 * GB-month quantity yet, so no index-storage price is shown.
 */
const RATES = [
  { title: 'Training', sub: 'LoRA fine-tuning, per accelerator minute', figure: '$0.05', unit: '/ min', note: '+ $0.25 per launch' },
  { title: 'Tokens', sub: 'text inference', figure: '$0.20', unit: '/ 1M in', note: '$0.90 / 1M out' },
  { title: 'Teacher work', sub: 'training data preparation', figure: '$1.00', unit: '/ 1M tokens', note: '' },
  { title: 'Embedding', sub: 'search preparation', figure: '$0.02', unit: '/ 1M tokens', note: '' },
  { title: 'Predictions', sub: 'tabular classification and regression', figure: '$1.00', unit: '/ 1 000 rows', note: '' },
  { title: 'Storage', sub: 'documents, datasets, artifacts', figure: '$0.10', unit: '/ GiB · month', note: '' },
]
</script>

<template>
  <section id="pricing" class="border-b border-border">
    <div class="mx-auto grid max-w-[var(--measure-1120px)] gap-10 px-6 py-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div class="flex flex-col gap-4">
        <span class="num flex items-center gap-1.5 text-[length:var(--type-fine)] font-semibold text-primary">Pricing<TodoBadge /></span>
        <h2 class="m-0 text-[length:var(--measure-2rem)] font-bold leading-[1.1] tracking-[-0.025em] text-pretty">Metered. Read straight off the ledger.</h2>
        <p class="m-0 max-w-[var(--measure-400px)] text-[length:var(--measure-1rem)] leading-relaxed text-muted-foreground text-pretty">No plans, no seats. A run shows its cost while it works and its billed minutes when it ends; the balance page says exactly what was spent. The rates shown are today's rate book.</p>
        <div class="mt-2 flex items-center gap-4 rounded-lg border border-primary/30 bg-primary/[0.05] p-4">
          <span class="num text-[length:var(--measure-2p1429rem)] font-semibold leading-none tracking-[-0.03em] text-primary">$5</span>
          <span class="flex flex-col gap-0.5"><span class="text-[length:var(--type-control)] font-semibold">usage credit every month</span><span class="num text-[length:var(--type-meta)] text-muted-foreground">no card to start · set your own overage limit</span></span>
        </div>
      </div>
      <div class="overflow-hidden rounded-lg border border-border bg-card">
        <div v-for="r in RATES" :key="r.title" class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-border px-5 py-4 last:border-b-0">
          <span class="flex min-w-0 flex-col gap-0.5"><span class="text-[length:var(--type-control)] font-semibold">{{ r.title }}</span><span class="text-[length:var(--measure-11p5px)] text-muted-foreground">{{ r.sub }}</span></span>
          <span class="flex flex-col items-end gap-0.5">
            <span class="num flex items-baseline gap-1.5"><span class="text-[length:var(--measure-1p4286rem)] font-semibold leading-none tracking-[-0.02em] text-primary">{{ r.figure }}</span><span class="text-[length:var(--type-meta)] text-muted-foreground">{{ r.unit }}</span></span>
            <span v-if="r.note" class="num text-[length:var(--type-fine)] text-muted-foreground">{{ r.note }}</span>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>
